import { Injectable } from '@angular/core';
import { ActionName, ActionValueType, MediaLibraryActionValue, MoveActionValue, VideoActionValue, WebsiteActionValue, WysiwygActionValue, FormBuilderActionValue, MediaItem } from '@newroom-connect/library/interfaces';

@Injectable({
  providedIn: 'root'
})
export class AreaElementActionService {
  /**
   * Cleans action value for API submission by removing unnecessary properties based on action type.
   *
   * @param actionName The name of the action to clean values for.
   * @param actionValue The action value object to be cleaned.
   *
   * @returns Cleaned action value suitable for API submission.
   */
  public cleanActionValue<T extends ActionName>(actionName: T, actionValue: ActionValueType<T>): ActionValueType<T> {
    if (!actionValue) {
      return actionValue;
    }

    const cleanedValue = { ...actionValue };

    // Remove common fields that should not be sent to the API
    delete cleanedValue['createdAt'];
    delete cleanedValue['updatedAt'];
    delete cleanedValue['id'];
    delete cleanedValue['actionId'];

    switch (actionName) {
      case ActionName.MEDIA_LIBRARY:
      case ActionName.MEDIA_SLIDER:
        return this.cleanMediaLibraryActionValue(cleanedValue as MediaLibraryActionValue) as ActionValueType<T>;

      case ActionName.MOVE:
        return this.cleanMoveActionValue(cleanedValue as MoveActionValue) as ActionValueType<T>;

      case ActionName.VIDEO:
        return this.cleanVideoActionValue(cleanedValue as VideoActionValue) as ActionValueType<T>;

      case ActionName.WEBSITE:
        return this.cleanWebsiteActionValue(cleanedValue as WebsiteActionValue) as ActionValueType<T>;

      case ActionName.FORM_BUILDER:
        return this.cleanFormBuilderActionValue(cleanedValue as FormBuilderActionValue) as ActionValueType<T>;

      case ActionName.WYSIWYG:
        return this.cleanWysiwygActionValue(cleanedValue as WysiwygActionValue) as ActionValueType<T>;

      default:
        return cleanedValue as ActionValueType<T>;
    }
  }

  /**
   * Cleans media library action value by ensuring proper structure and removing unnecessary properties.
   *
   * @param value The media library action value to clean.
   *
   * @returns Cleaned media library action value.
   */
  private cleanMediaLibraryActionValue(value: MediaLibraryActionValue): MediaLibraryActionValue {
    const cleanedValue: MediaLibraryActionValue = {
      popupTitle: value.popupTitle,
      mediaLibraryData: {
        data: [],
        openInFullscreen: false,
        gridLayoutStretched: false,
        disableDownload: false
      }
    };

    if (value.mediaLibraryData) {
      // If mediaLibraryData is an array, convert it to the proper object structure
      if (Array.isArray(value.mediaLibraryData)) {
        cleanedValue.mediaLibraryData = {
          data: this.cleanMediaItems(value.mediaLibraryData),
          openInFullscreen: false,
          gridLayoutStretched: false,
          disableDownload: false
        };
      } else if (value.mediaLibraryData.data) {
        cleanedValue.mediaLibraryData = {
          data: this.cleanMediaItems(value.mediaLibraryData.data),
          openInFullscreen: value.mediaLibraryData.openInFullscreen ?? false,
          gridLayoutStretched: value.mediaLibraryData.gridLayoutStretched ?? false,
          disableDownload: value.mediaLibraryData.disableDownload ?? false
        };
      }
    }

    return cleanedValue;
  }

  /**
   * Cleans media items by removing file and thumbnail objects, keeping only IDs.
   *
   * @param items The media items to clean.
   *
   * @returns Cleaned media items.
   */
  private cleanMediaItems(items: MediaItem[]): MediaItem[] {
    return items.map(item => ({
      type: item.type,
      label: item.label,
      isSelected: item.isSelected,
      description: item.description,
      thumbnail: item.thumbnail,
      fileId: 'fileId' in item ? item.fileId : null,
      thumbnailId: 'thumbnailId' in item ? item.thumbnailId : null,
      isThumbnailAsDetailImage: 'isThumbnailAsDetailImage' in item ? item.isThumbnailAsDetailImage : null,
      items: 'items' in item ? this.cleanMediaItems(item.items ?? []) : null
    }));
  }

  /**
   * Cleans move action value by keeping only required properties.
   *
   * @param value The move action value to clean.
   *
   * @returns Cleaned move action value.
   */
  private cleanMoveActionValue(value: MoveActionValue): MoveActionValue {
    return {
      moveAreaId: value.moveAreaId,
      popupTitle: value.popupTitle
    };
  }

  /**
   * Cleans video action value by keeping only required properties.
   *
   * @param value The video action value to clean.
   *
   * @returns Cleaned video action value.
   */
  private cleanVideoActionValue(value: VideoActionValue): VideoActionValue {
    return {
      videoType: value.videoType,
      videoFileId: value.videoFileId,
      youtubeUrl: value.youtubeUrl,
      popupTitle: value.popupTitle
    };
  }

  /**
   * Cleans website action value by keeping only required properties.
   *
   * @param value The website action value to clean.
   *
   * @returns Cleaned website action value.
   */
  private cleanWebsiteActionValue(value: WebsiteActionValue): WebsiteActionValue {
    return {
      websiteUrl: value.websiteUrl,
      openInNewTab: value.openInNewTab,
      popupTitle: value.popupTitle
    };
  }

  /**
   * Cleans form builder action value by keeping only required properties.
   *
   * @param value The form builder action value to clean.
   *
   * @returns Cleaned form builder action value.
   */
  private cleanFormBuilderActionValue(value: FormBuilderActionValue): FormBuilderActionValue {
    return {
      formBuilderData: value.formBuilderData,
      popupTitle: value.popupTitle
    };
  }

  /**
   * Cleans WYSIWYG action value by keeping only required properties.
   *
   * @param value The WYSIWYG action value to clean.
   *
   * @returns Cleaned WYSIWYG action value.
   */
  private cleanWysiwygActionValue(value: WysiwygActionValue): WysiwygActionValue {
    return {
      wysiwygContent: value.wysiwygContent,
      popupTitle: value.popupTitle
    };
  }
}
